import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Clock, CheckCircle, Truck, XCircle, ChevronRight, ShoppingBag, Star, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersPage = () => {
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Rating states
  const [ratingModal, setRatingModal] = useState(null); // {order, item}
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratedItems, setRatedItems] = useState(new Set());

  const openRatingModal = (order, item) => {
    setRatingModal({ order, item });
    setRatingValue(0);
    setRatingHover(0);
    setRatingComment('');
  };

  const submitRating = async () => {
    if (ratingValue === 0) {
      toast.error('Lütfen bir puan seçin');
      return;
    }
    setSubmittingRating(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: ratingModal.item.product_id,
        store_id: ratingModal.item.seller_id || ratingModal.order.store_id,
        order_item_id: ratingModal.item.id,
        rating: ratingValue,
        comment: ratingComment || null
      });
      toast.success('Değerlendirmeniz kaydedildi!');
      setRatedItems(prev => new Set([...prev, ratingModal.item.id]));
      setRatingModal(null);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Değerlendirme gönderilemedi';
      toast.error(msg);
    } finally {
      setSubmittingRating(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'shipped':
        return <Truck className="h-5 w-5 text-blue-500" />;
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Package className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'confirmed':
        return 'Onaylandı';
      case 'shipped':
        return 'Kargoda';
      case 'delivered':
        return 'Teslim Edildi';
      case 'cancelled':
        return 'İptal Edildi';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'confirmed':
        return 'bg-green-100 text-green-700';
      case 'shipped':
        return 'bg-blue-100 text-blue-700';
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <Package className="h-24 w-24 text-slate-300 mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-slate-700 mb-2">Giriş Yapın</h2>
        <p className="text-slate-500 mb-6">Siparişlerinizi görmek için giriş yapın</p>
        <Link to="/login">
          <Button className="bg-orange-500 hover:bg-orange-600">Giriş Yap</Button>
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-32 bg-slate-200 rounded" />
          <div className="h-32 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="orders-page">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Siparişlerim</h1>

        {orders.length > 0 ? (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} data-testid={`order-${order.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(order.status)}
                      <div>
                        <p className="font-mono font-bold">#{order.id.slice(0, 8).toUpperCase()}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(order.created_at).toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(order.status)}>
                        {getStatusText(order.status)}
                      </Badge>
                      <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                        {order.payment_status === 'paid' ? 'Ödendi' : 'Ödeme Bekliyor'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Order Items */}
                  <div className="space-y-3 mb-4">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                          {item.product_image ? (
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <Package className="h-6 w-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link to={`/products/${item.product_id}`} className="font-medium hover:text-orange-500 line-clamp-1">
                            {item.product_name}
                          </Link>
                          <p className="text-sm text-slate-500">
                            {item.quantity} adet x {formatPrice(item.price)}
                          </p>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1">
                          <p className="font-bold text-orange-600">{formatPrice(item.subtotal)}</p>
                          {order.status === 'delivered' && !ratedItems.has(item.id) && (
                            <button
                              onClick={(e) => { e.preventDefault(); openRatingModal(order, item); }}
                              className="text-xs text-orange-500 hover:text-orange-600 flex items-center gap-1 font-medium"
                              data-testid={`rate-btn-${item.id}`}
                            >
                              <Star className="h-3 w-3" /> Değerlendir
                            </button>
                          )}
                          {ratedItems.has(item.id) && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" /> Değerlendirildi
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Shipping Info */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-slate-500 mb-1">Teslimat Adresi</p>
                    <p className="font-medium">{order.shipping_address}</p>
                    <p className="text-sm text-slate-600">{order.shipping_city} | {order.shipping_phone}</p>
                  </div>

                  {/* Total */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="font-medium text-slate-600">Toplam</span>
                    <span className="text-xl font-bold text-orange-600">{formatPrice(order.total_amount)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <ShoppingBag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-600 mb-2">Henüz sipariş yok</h3>
            <p className="text-slate-500 mb-4">İlk siparişinizi verin!</p>
            <Link to="/products">
              <Button className="bg-orange-500 hover:bg-orange-600">
                Alışverişe Başla
              </Button>
            </Link>
          </Card>
        )}
      </div>

      {/* Rating Modal */}
      {ratingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRatingModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="rating-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Satıcıyı Değerlendir</h3>
            <p className="text-sm text-slate-500 mb-4">{ratingModal.item.product_name}</p>

            {/* Stars */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRatingValue(star)}
                  onMouseEnter={() => setRatingHover(star)}
                  onMouseLeave={() => setRatingHover(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${star}`}
                >
                  <Star
                    className={`h-10 w-10 transition-colors ${
                      star <= (ratingHover || ratingValue)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-slate-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-slate-500 mb-4">
              {ratingValue === 0 && 'Puan seçin'}
              {ratingValue === 1 && 'Çok kötü'}
              {ratingValue === 2 && 'Kötü'}
              {ratingValue === 3 && 'Orta'}
              {ratingValue === 4 && 'İyi'}
              {ratingValue === 5 && 'Mükemmel'}
            </p>

            {/* Comment */}
            <textarea
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              placeholder="Yorumunuzu yazın (opsiyonel)..."
              className="w-full border rounded-xl p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 mb-4"
              data-testid="rating-comment"
            />

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setRatingModal(null)}
                className="flex-1 py-2.5 px-4 border rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={submitRating}
                disabled={ratingValue === 0 || submittingRating}
                className="flex-1 py-2.5 px-4 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="submit-rating-btn"
              >
                {submittingRating ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
